/**
 * Weekly Work Recap Component
 * ============================
 * Shows "What Was Done This Week" for 5 Pillars meetings.
 * Groups entries by day with expandable details.
 * Features:
 * - Live timer for current active shift
 * - Rich metrics per day (tasks, features, bugs, support tickets)
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { format, formatDistanceToNow, parseISO, startOfDay, isSameDay, isToday, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subWeeks, subMonths, addDays } from 'date-fns';
import {
  Clock, Calendar, CheckCircle, AlertTriangle, ChevronDown, ChevronUp,
  Image, FileText, Loader2, Target, ListChecks, User, Pencil, Plus, X, Save,
  PlayCircle, Bug, Sparkles, MessageSquare, PlusCircle, Zap, Trash2, Pause,
  Filter, CalendarDays, ChevronLeft, ChevronRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ScrollArea } from '../ui/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Keywords for categorizing entries
const CATEGORY_KEYWORDS = {
  feature: ['feature', 'implement', 'build', 'create', 'add', 'new'],
  bug: ['bug', 'fix', 'fixed', 'issue', 'error', 'resolve', 'resolved'],
  support: ['support', 'ticket', 'customer', 'respond', 'help', 'inquiry']
};

// Categorize an entry based on description keywords
const categorizeEntry = (description) => {
  const text = description.toLowerCase();
  const categories = { feature: 0, bug: 0, support: 0 };
  
  Object.entries(CATEGORY_KEYWORDS).forEach(([category, keywords]) => {
    keywords.forEach(kw => {
      if (text.includes(kw)) categories[category]++;
    });
  });
  
  return categories;
};

export const WeeklyRecapSection = ({ meetingId = null, editable = false, compact = false }) => {
  const [loading, setLoading] = useState(true);
  const [recap, setRecap] = useState(null);
  const [expandedDays, setExpandedDays] = useState({});
  const [editableSummary, setEditableSummary] = useState('');
  const [editingDay, setEditingDay] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);
  const [dayEditForm, setDayEditForm] = useState({
    summary: '',
    blockers: '',
    whats_next: '',
    started_at: '',
    ended_at: ''
  });
  
  // Date filter state
  const [dateFilter, setDateFilter] = useState('this_week'); // 'this_week', 'last_week', 'this_month', 'last_month', 'custom'
  const [customDateRange, setCustomDateRange] = useState({
    start: format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd'),
    end: format(endOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd')
  });
  
  // Calculate date range based on filter
  const dateRange = useMemo(() => {
    const today = new Date();
    let start, end;
    
    switch (dateFilter) {
      case 'this_week':
        start = startOfWeek(today, { weekStartsOn: 1 }); // Monday
        end = endOfWeek(today, { weekStartsOn: 1 }); // Sunday
        break;
      case 'last_week':
        const lastWeek = subWeeks(today, 1);
        start = startOfWeek(lastWeek, { weekStartsOn: 1 });
        end = endOfWeek(lastWeek, { weekStartsOn: 1 });
        break;
      case 'this_month':
        start = startOfMonth(today);
        end = endOfMonth(today);
        break;
      case 'last_month':
        const lastMonth = subMonths(today, 1);
        start = startOfMonth(lastMonth);
        end = endOfMonth(lastMonth);
        break;
      case 'custom':
        start = parseISO(customDateRange.start);
        end = parseISO(customDateRange.end);
        break;
      default:
        start = startOfWeek(today, { weekStartsOn: 1 });
        end = endOfWeek(today, { weekStartsOn: 1 });
    }
    
    return { start, end };
  }, [dateFilter, customDateRange]);
  
  // Generate all days in the selected range (for showing empty days too)
  const allDaysInRange = useMemo(() => {
    const days = [];
    let current = new Date(dateRange.start);
    const endDate = new Date(dateRange.end);
    
    while (current <= endDate) {
      days.push({
        date: new Date(current),
        dayKey: format(current, 'yyyy-MM-dd')
      });
      current = addDays(current, 1);
    }
    
    return days.reverse(); // Most recent first
  }, [dateRange]);
  
  // Active session state for live timer
  const [activeSession, setActiveSession] = useState(null);
  const [teamActiveSessions, setTeamActiveSessions] = useState([]);
  const [liveElapsed, setLiveElapsed] = useState(0);
  const timerRef = useRef(null);
  
  // Role-based access control
  const [canEdit, setCanEdit] = useState(false);
  
  // Add Entry modal state
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [addEntryForm, setAddEntryForm] = useState({
    entry_type: 'task_completed',
    description: '',
    hub_id: '',
    is_mvp: false
  });
  
  // Edit Entry form state
  const [editEntryForm, setEditEntryForm] = useState({
    entry_type: 'task_completed',
    description: '',
    hub_id: '',
    is_mvp: false
  });

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  // Fetch active session for live timer (own session if can edit)
  const fetchActiveSession = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API}/five-pillars-hub/workday/my-active`,
        { headers: getHeaders() }
      );
      setActiveSession(response.data.session);
    } catch (error) {
      // If user can't access my-active (not business ops), that's OK
      console.log('No own active session access');
    }
  }, [getHeaders]);

  // Fetch team active sessions (for leadership to see who's working)
  const fetchTeamActiveSessions = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API}/five-pillars-hub/workday/team-active`,
        { headers: getHeaders() }
      );
      setTeamActiveSessions(response.data.sessions || []);
    } catch (error) {
      // If user can't access team-active, that's OK
      console.log('No team active session access');
    }
  }, [getHeaders]);

  const fetchRecap = useCallback(async () => {
    try {
      // Build query params including date range
      const params = new URLSearchParams();
      if (meetingId) params.append('since_meeting_id', meetingId);
      params.append('start_date', dateRange.start.toISOString());
      params.append('end_date', dateRange.end.toISOString());
      
      const queryString = params.toString() ? `?${params.toString()}` : '';
      const response = await axios.get(
        `${API}/five-pillars-hub/workday/meeting-recap${queryString}`,
        { headers: getHeaders() }
      );
      setRecap(response.data);
      setEditableSummary(response.data.auto_summary || '');
      setCanEdit(response.data.can_edit || false);
    } catch (error) {
      console.error('Error fetching weekly recap:', error);
    } finally {
      setLoading(false);
    }
  }, [meetingId, getHeaders, dateRange]);

  useEffect(() => {
    setLoading(true);
    fetchRecap();
    fetchActiveSession();
    fetchTeamActiveSessions();
  }, [fetchRecap, fetchActiveSession, fetchTeamActiveSessions]);

  // Live timer effect - handles paused state and accumulated time
  useEffect(() => {
    if (activeSession) {
      const accumulated = activeSession.accumulated_seconds || 0;
      
      if (activeSession.status === 'paused') {
        // When paused, just show accumulated time
        setLiveElapsed(accumulated);
        if (timerRef.current) clearInterval(timerRef.current);
      } else if (activeSession.status === 'active') {
        // When active, calculate from last_resumed_at or started_at
        const resumeTime = activeSession.last_resumed_at || activeSession.started_at;
        const startTime = new Date(resumeTime).getTime();
        
        const updateElapsed = () => {
          const now = Date.now();
          const currentSegment = Math.floor((now - startTime) / 1000);
          setLiveElapsed(accumulated + currentSegment);
        };
        
        updateElapsed();
        timerRef.current = setInterval(updateElapsed, 1000);
        
        return () => {
          if (timerRef.current) clearInterval(timerRef.current);
        };
      }
    } else {
      setLiveElapsed(0);
      if (timerRef.current) clearInterval(timerRef.current);
    }
  }, [activeSession]);

  // Format live elapsed time as HH:MM:SS
  const formatLiveTimer = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Check if a date is today
  const isCurrentDay = (date) => {
    return isToday(date);
  };

  // Group sessions by day with rich metrics
  const groupedByDay = useMemo(() => {
    if (!recap?.daily_breakdown) return [];
    
    const dayMap = new Map();
    
    recap.daily_breakdown.forEach(session => {
      const sessionDate = parseISO(session.date);
      const dayKey = format(sessionDate, 'yyyy-MM-dd');
      
      if (!dayMap.has(dayKey)) {
        dayMap.set(dayKey, {
          date: sessionDate,
          dayKey,
          sessions: [],
          totalSeconds: 0,
          totalTasks: 0,
          hasBlockers: false,
          allEntries: [],
          allBlockers: [],
          // Rich metrics
          metrics: {
            tasksCompleted: 0,
            tasksAdded: 0,
            featuresCompleted: 0,
            featuresCreated: 0,
            bugsFix: 0,
            supportTickets: 0
          }
        });
      }
      
      const dayData = dayMap.get(dayKey);
      dayData.sessions.push(session);
      dayData.totalSeconds += session.duration?.total_seconds || 0;
      dayData.totalTasks += session.tasks_completed || 0;
      dayData.metrics.tasksCompleted += session.tasks_completed || 0;
      
      if (session.blockers) {
        dayData.hasBlockers = true;
        dayData.allBlockers.push(session.blockers);
      }
      
      // Collect all manual entries and categorize them
      if (session.manual_entries && session.manual_entries.length > 0) {
        session.manual_entries.forEach(entry => {
          dayData.allEntries.push(entry);
          dayData.metrics.tasksAdded++;
          
          // Categorize based on description
          const categories = categorizeEntry(entry.description || '');
          dayData.metrics.featuresCreated += categories.feature > 0 ? 1 : 0;
          dayData.metrics.bugsFix += categories.bug > 0 ? 1 : 0;
          dayData.metrics.supportTickets += categories.support > 0 ? 1 : 0;
        });
      }
      
      // Also add session summary as an entry if exists
      if (session.summary) {
        dayData.allEntries.push({
          id: `summary-${session.id}`,
          description: session.summary,
          type: 'summary',
          created_at: session.date,
          session_id: session.id
        });
        
        // Categorize summary too
        const categories = categorizeEntry(session.summary || '');
        dayData.metrics.featuresCompleted += categories.feature > 0 ? 1 : 0;
        dayData.metrics.bugsFix += categories.bug > 0 ? 1 : 0;
        dayData.metrics.supportTickets += categories.support > 0 ? 1 : 0;
      }
    });
    
    // Convert to array and sort by date (most recent first)
    return Array.from(dayMap.values()).sort((a, b) => b.date - a.date);
  }, [recap?.daily_breakdown]);

  const toggleDay = (dayKey) => {
    setExpandedDays(prev => ({ ...prev, [dayKey]: !prev[dayKey] }));
  };

  // Helper to format datetime for input - converts UTC to local time for display
  const formatDateTimeForInput = (isoString) => {
    if (!isoString) return '';
    try {
      // Parse the ISO string (which is in UTC) and display in local time
      const date = new Date(isoString);
      if (isNaN(date.getTime())) return '';
      
      // Format as YYYY-MM-DDTHH:MM in LOCAL time for datetime-local input
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch {
      return '';
    }
  };

  // Calculate duration between start and end times
  const calculateDuration = () => {
    if (!dayEditForm.started_at || !dayEditForm.ended_at) return null;
    try {
      const start = new Date(dayEditForm.started_at);
      const end = new Date(dayEditForm.ended_at);
      const diffMs = end - start;
      if (diffMs < 0) return null;
      const hours = Math.floor(diffMs / (1000 * 60 * 60));
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      return { hours, minutes, formatted: `${hours}h ${minutes}m` };
    } catch {
      return null;
    }
  };

  // Apply time preset (sets start and end times for the editing day's date in LOCAL time)
  const applyTimePreset = (startHour, startMin, endHour, endMin) => {
    if (!editingDay?.date) return;
    const baseDate = new Date(editingDay.date);
    
    const startDate = new Date(baseDate);
    startDate.setHours(startHour, startMin, 0, 0);
    
    const endDate = new Date(baseDate);
    endDate.setHours(endHour, endMin, 0, 0);
    
    // Format as local time for datetime-local input
    const formatLocal = (d) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const hours = String(d.getHours()).padStart(2, '0');
      const minutes = String(d.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };
    
    setDayEditForm(prev => ({
      ...prev,
      started_at: formatLocal(startDate),
      ended_at: formatLocal(endDate)
    }));
  };

  const openEditDay = (dayData, session) => {
    setEditingDay({ ...dayData, session });
    setDayEditForm({
      summary: session?.summary || session?.end_summary || '',
      blockers: session?.blockers || '',
      whats_next: session?.whats_next || '',
      started_at: formatDateTimeForInput(session?.started_at || dayData?.date),
      ended_at: formatDateTimeForInput(session?.ended_at)
    });
  };

  const saveDay = async () => {
    if (!editingDay?.session?.id) return;
    
    try {
      // Build payload with the datetime values
      const payload = {
        summary: dayEditForm.summary,
        blockers: dayEditForm.blockers,
        whats_next: dayEditForm.whats_next
      };
      
      // For time values, the datetime-local input gives us local time as "YYYY-MM-DDTHH:MM"
      // We need to convert this to UTC before sending to the server
      if (dayEditForm.started_at) {
        // Parse as local time and convert to UTC ISO string
        const localDate = new Date(dayEditForm.started_at);
        payload.started_at = localDate.toISOString();
      }
      if (dayEditForm.ended_at) {
        const localDate = new Date(dayEditForm.ended_at);
        payload.ended_at = localDate.toISOString();
      }
      
      await axios.patch(
        `${API}/five-pillars-hub/workday/session/${editingDay.session.id}`,
        payload,
        { headers: getHeaders() }
      );
      toast.success('Day updated');
      setEditingDay(null);
      fetchRecap();
    } catch (error) {
      console.error('Error saving day:', error);
      toast.error('Failed to save');
    }
  };

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  // Get today's date key in local timezone
  const todayKey = format(new Date(), 'yyyy-MM-dd');
  
  // Calculate how active session time splits across days (handles midnight crossover)
  const activeSessionDaySplit = useMemo(() => {
    if (!activeSession?.started_at) return null;
    
    const sessionStart = parseISO(activeSession.started_at);
    const now = new Date();
    const accumulated = activeSession.accumulated_seconds || 0;
    
    // For paused sessions, use accumulated time only
    // For active sessions, add current segment
    let totalElapsed = accumulated;
    if (activeSession.status === 'active') {
      const resumeTime = activeSession.last_resumed_at || activeSession.started_at;
      const resumeDate = new Date(resumeTime);
      totalElapsed += Math.floor((now - resumeDate) / 1000);
    }
    
    // Calculate time per day by finding midnight boundaries
    const dayBreakdown = {};
    
    // Start from session start and walk through each day
    let currentTime = new Date(sessionStart);
    let remainingSeconds = totalElapsed;
    
    while (remainingSeconds > 0 && currentTime <= now) {
      const dayKey = format(currentTime, 'yyyy-MM-dd');
      
      // Find end of this day (midnight) or now, whichever is earlier
      const endOfDay = new Date(currentTime);
      endOfDay.setHours(23, 59, 59, 999);
      const dayEnd = endOfDay < now ? new Date(endOfDay.getTime() + 1) : now; // Start of next day or now
      
      // Calculate seconds in this day
      const secondsInDay = Math.floor((dayEnd - currentTime) / 1000);
      const secondsToAttribute = Math.min(secondsInDay, remainingSeconds);
      
      if (secondsToAttribute > 0) {
        dayBreakdown[dayKey] = (dayBreakdown[dayKey] || 0) + secondsToAttribute;
      }
      
      remainingSeconds -= secondsToAttribute;
      
      // Move to start of next day
      currentTime = new Date(dayEnd);
      if (currentTime <= endOfDay) {
        currentTime.setDate(currentTime.getDate() + 1);
        currentTime.setHours(0, 0, 0, 0);
      }
    }
    
    return {
      dayBreakdown,
      sessionStartKey: format(sessionStart, 'yyyy-MM-dd'),
      totalElapsed
    };
  }, [activeSession]);
  
  // Calculate TODAY's live elapsed time (only hours since midnight today)
  const todayLiveElapsed = useMemo(() => {
    if (!activeSession || activeSession.status !== 'active') return 0;
    if (!activeSessionDaySplit?.dayBreakdown) return 0;
    return activeSessionDaySplit.dayBreakdown[todayKey] || 0;
  }, [activeSession, activeSessionDaySplit, todayKey]);
  
  // Live timer for today - updates every second for today's portion only
  const [todayLiveTimer, setTodayLiveTimer] = useState(0);
  
  // Live timer for team member's today hours (for leadership view)
  const [teamTodayTimer, setTeamTodayTimer] = useState(0);
  
  // Calculate team member's today hours (for leadership viewing someone else's session)
  useEffect(() => {
    const teamSession = teamActiveSessions[0]; // Primary team member being displayed
    if (teamSession?.status === 'active') {
      const updateTeamTodayTimer = () => {
        const now = new Date();
        const todayStart = new Date(now);
        todayStart.setHours(0, 0, 0, 0); // Local midnight
        
        const resumeTime = teamSession.last_resumed_at || teamSession.started_at;
        const resumeDate = new Date(resumeTime);
        
        // Effective start for today is either session resume time or start of today (local), whichever is later
        const effectiveStartForToday = resumeDate > todayStart ? resumeDate : todayStart;
        
        if (effectiveStartForToday <= now) {
          const todaySeconds = Math.floor((now - effectiveStartForToday) / 1000);
          setTeamTodayTimer(Math.max(0, todaySeconds));
        } else {
          setTeamTodayTimer(0);
        }
      };
      
      updateTeamTodayTimer();
      const interval = setInterval(updateTeamTodayTimer, 1000);
      return () => clearInterval(interval);
    } else {
      setTeamTodayTimer(0);
    }
  }, [teamActiveSessions]);
  
  useEffect(() => {
    if (activeSession?.status === 'active') {
      const updateTodayTimer = () => {
        const now = new Date();
        const todayStart = new Date(now);
        todayStart.setHours(0, 0, 0, 0);
        
        // Calculate time worked today
        const sessionStart = parseISO(activeSession.started_at);
        const resumeTime = activeSession.last_resumed_at || activeSession.started_at;
        const resumeDate = new Date(resumeTime);
        
        // Effective start for today is either session resume time or start of today, whichever is later
        const effectiveStartForToday = resumeDate > todayStart ? resumeDate : todayStart;
        
        // If session started before today, we need to account for accumulated time from previous days
        // But for the LIVE timer, we only show today's elapsed time
        if (effectiveStartForToday <= now) {
          const todaySeconds = Math.floor((now - effectiveStartForToday) / 1000);
          setTodayLiveTimer(Math.max(0, todaySeconds));
        } else {
          setTodayLiveTimer(0);
        }
      };
      
      updateTodayTimer();
      const interval = setInterval(updateTodayTimer, 1000);
      return () => clearInterval(interval);
    } else if (activeSession?.status === 'paused') {
      // When paused, show accumulated time for today
      setTodayLiveTimer(activeSessionDaySplit?.dayBreakdown?.[todayKey] || 0);
    } else {
      setTodayLiveTimer(0);
    }
  }, [activeSession, todayKey, activeSessionDaySplit]);
    
  // Build display list - merge active session time into appropriate days
  const displayDays = useMemo(() => {
    // Start with grouped days from recap
    const dayMap = new Map();
    
    // Add all days from recap
    groupedByDay.forEach(day => {
      dayMap.set(day.dayKey, { ...day });
    });
    
    // If there's an active session, add/merge its time per day (only for days within range)
    if (activeSession && activeSessionDaySplit?.dayBreakdown) {
      Object.entries(activeSessionDaySplit.dayBreakdown).forEach(([dayKey, seconds]) => {
        const dayDate = parseISO(dayKey);
        
        // Only add if within the selected date range
        const isInRange = allDaysInRange.some(d => d.dayKey === dayKey);
        if (!isInRange) return;
        
        if (dayMap.has(dayKey)) {
          // Day exists - the active session time is already being tracked live
          // Don't add to totalSeconds as it's shown separately as LIVE
        } else {
          // Day doesn't exist in recap - create new entry
          dayMap.set(dayKey, {
            date: dayDate,
            dayKey,
            sessions: [],
            totalSeconds: 0, // Will show LIVE timer instead
            totalTasks: 0,
            hasBlockers: false,
            allEntries: [],
            allBlockers: [],
            metrics: { tasksCompleted: 0, tasksAdded: 0, featuresCreated: 0, bugsFix: 0, supportTickets: 0 },
            isActiveOnly: true
          });
        }
      });
    }
    
    // Add all days in the selected range (even empty ones)
    allDaysInRange.forEach(({ date, dayKey }) => {
      if (!dayMap.has(dayKey)) {
        dayMap.set(dayKey, {
          date,
          dayKey,
          sessions: [],
          totalSeconds: 0,
          totalTasks: 0,
          hasBlockers: false,
          allEntries: [],
          allBlockers: [],
          metrics: { tasksCompleted: 0, tasksAdded: 0, featuresCreated: 0, bugsFix: 0, supportTickets: 0 },
          isEmpty: true // Flag for empty days
        });
      }
    });
    
    // Filter to only include days within the selected range
    const rangeKeys = new Set(allDaysInRange.map(d => d.dayKey));
    const filteredDays = Array.from(dayMap.values()).filter(d => rangeKeys.has(d.dayKey));
    
    // Sort by date (most recent first)
    return filteredDays.sort((a, b) => b.date - a.date);
  }, [groupedByDay, activeSession, activeSessionDaySplit, allDaysInRange]);
  
  // Check if a day has active session time (for showing LIVE badge)
  const dayHasActiveTime = (dayKey) => {
    return activeSession && activeSessionDaySplit?.dayBreakdown?.[dayKey] > 0;
  };
  
  // Get active session seconds for a specific day
  const getActiveSecondsForDay = (dayKey) => {
    if (!activeSession || !activeSessionDaySplit?.dayBreakdown) return 0;
    return activeSessionDaySplit.dayBreakdown[dayKey] || 0;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  if (!recap) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          No work data available for this period
        </CardContent>
      </Card>
    );
  }

  // Start/End shift handlers
  const handleStartShift = async () => {
    try {
      await axios.post(
        `${API}/five-pillars-hub/workday/start`,
        { selected_task_ids: [], notes: '' },
        { headers: getHeaders() }
      );
      toast.success('Shift started!');
      fetchActiveSession();
      fetchRecap();
    } catch (error) {
      console.error('Error starting shift:', error);
      toast.error(error.response?.data?.detail || 'Failed to start shift');
    }
  };

  const handleEndShift = async () => {
    try {
      await axios.post(
        `${API}/five-pillars-hub/workday/end`,
        { summary: 'Shift ended', tasks_completed_ids: [], attachment_ids: [] },
        { headers: getHeaders() }
      );
      toast.success('Shift ended!');
      setActiveSession(null);
      setLiveElapsed(0);
      fetchRecap();
    } catch (error) {
      console.error('Error ending shift:', error);
      toast.error(error.response?.data?.detail || 'Failed to end shift');
    }
  };

  const handlePauseShift = async () => {
    try {
      const response = await axios.post(
        `${API}/five-pillars-hub/workday/pause`,
        {},
        { headers: getHeaders() }
      );
      toast.success('Shift paused');
      setActiveSession(response.data.session);
    } catch (error) {
      console.error('Error pausing shift:', error);
      toast.error(error.response?.data?.detail || 'Failed to pause shift');
    }
  };

  const handleResumeShift = async () => {
    try {
      const response = await axios.post(
        `${API}/five-pillars-hub/workday/resume`,
        {},
        { headers: getHeaders() }
      );
      toast.success('Shift resumed');
      setActiveSession(response.data.session);
    } catch (error) {
      console.error('Error resuming shift:', error);
      toast.error(error.response?.data?.detail || 'Failed to resume shift');
    }
  };

  const handleAddEntry = async () => {
    if (!addEntryForm.description.trim()) {
      toast.error('Please enter a description');
      return;
    }
    try {
      await axios.post(
        `${API}/five-pillars-hub/workday/quick-add`,
        addEntryForm,
        { headers: getHeaders() }
      );
      toast.success('Entry added!');
      setAddEntryForm({
        entry_type: 'task_completed',
        description: '',
        hub_id: '',
        is_mvp: false
      });
      setShowAddEntry(false);
      fetchRecap();
      fetchActiveSession();
    } catch (error) {
      console.error('Error adding entry:', error);
      toast.error(error.response?.data?.detail || 'Failed to add entry');
    }
  };

  const openEditEntry = (entry) => {
    setEditingEntry(entry);
    setEditEntryForm({
      entry_type: entry.entry_type || 'task_completed',
      description: entry.description || '',
      hub_id: entry.hub_id || '',
      is_mvp: entry.is_mvp || false
    });
  };

  const handleUpdateEntry = async () => {
    if (!editEntryForm.description.trim()) {
      toast.error('Please enter a description');
      return;
    }
    try {
      await axios.patch(
        `${API}/five-pillars-hub/workday/entry/${editingEntry.id}`,
        editEntryForm,
        { headers: getHeaders() }
      );
      toast.success('Entry updated!');
      setEditingEntry(null);
      fetchRecap();
    } catch (error) {
      console.error('Error updating entry:', error);
      toast.error(error.response?.data?.detail || 'Failed to update entry');
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Delete this entry?')) {
      return;
    }
    try {
      await axios.delete(
        `${API}/five-pillars-hub/workday/entry/${entryId}`,
        { headers: getHeaders() }
      );
      toast.success('Entry deleted');
      setEditingEntry(null);
      fetchRecap();
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete entry');
    }
  };

  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm('Are you sure you want to delete this shift? This cannot be undone.')) {
      return;
    }
    try {
      await axios.delete(
        `${API}/five-pillars-hub/workday/session/${sessionId}`,
        { headers: getHeaders() }
      );
      toast.success('Shift deleted');
      fetchRecap();
    } catch (error) {
      console.error('Error deleting session:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete shift');
    }
  };

  const handleClearAllSessions = async () => {
    if (!window.confirm('⚠️ Are you sure you want to delete ALL shifts? This will clear all work history and cannot be undone!')) {
      return;
    }
    if (!window.confirm('This is your final warning. Delete ALL work history?')) {
      return;
    }
    try {
      const response = await axios.delete(
        `${API}/five-pillars-hub/workday/sessions/all`,
        { headers: getHeaders() }
      );
      toast.success(`Deleted ${response.data.deleted_count} sessions`);
      fetchRecap();
    } catch (error) {
      console.error('Error clearing sessions:', error);
      toast.error(error.response?.data?.detail || 'Failed to clear sessions');
    }
  };

  if (compact) {
    const isPaused = activeSession?.status === 'paused';
    // Check if there's any team member currently working (for leadership view)
    const hasTeamWorkingNow = teamActiveSessions.length > 0;
    const teamWorkingPerson = teamActiveSessions[0];
    
    // Calculate days with work vs total days in range
    const daysWithWork = displayDays.filter(d => d.totalSeconds > 0 || dayHasActiveTime(d.dayKey)).length;
    
    return (
      <TooltipProvider>
        <div className="space-y-3">
          {/* Date Filter - Compact */}
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded-lg p-0.5">
              <Button
                variant={dateFilter === 'this_week' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setDateFilter('this_week')}
                className="h-6 text-[10px] px-2"
              >
                This Week
              </Button>
              <Button
                variant={dateFilter === 'last_week' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setDateFilter('last_week')}
                className="h-6 text-[10px] px-2"
              >
                Last Week
              </Button>
              <Button
                variant={dateFilter === 'this_month' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setDateFilter('this_month')}
                className="h-6 text-[10px] px-2"
              >
                Month
              </Button>
            </div>
            <span className="text-[10px] text-muted-foreground">
              {format(dateRange.start, 'MMM d')} - {format(dateRange.end, 'MMM d')}
            </span>
          </div>
          
          {/* Team Live Status Banner - Show to everyone when someone is working */}
          {!canEdit && hasTeamWorkingNow && (
            <div className="flex items-center gap-2 p-2 bg-green-50 dark:bg-green-900/30 rounded-lg border border-green-200 dark:border-green-700">
              <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-sm font-medium text-green-700 dark:text-green-300">
                {teamWorkingPerson?.user_name || 'Team member'} is working
              </span>
              <div className="flex items-center gap-2 ml-auto">
                <Badge className="bg-green-600 text-white text-xs">
                  Today: {formatLiveTimer(teamTodayTimer)}
                </Badge>
                <Badge variant="outline" className="text-xs text-green-700 dark:text-green-300">
                  Total: {teamWorkingPerson?.live_duration?.formatted || '...'}
                </Badge>
              </div>
            </div>
          )}
          
          {/* Shift Controls - Only show if user can edit */}
          {canEdit && (
            <div className="flex justify-between items-center gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClearAllSessions}
                    className="h-7 text-xs text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <Trash2 className="h-3 w-3 mr-1" />
                    Clear All
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Delete all work history (test data)</TooltipContent>
              </Tooltip>
              
              <div className="flex items-center gap-1">
                {activeSession ? (
                  <>
                    {/* Add Entry Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowAddEntry(true)}
                      className="h-7 text-xs"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add
                    </Button>
                    
                    {/* Pause/Resume Button */}
                    {isPaused ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleResumeShift}
                        className="h-7 text-xs border-green-500 text-green-600 hover:bg-green-50"
                      >
                        <PlayCircle className="h-3 w-3 mr-1" />
                        Resume
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handlePauseShift}
                        className="h-7 text-xs border-amber-500 text-amber-600 hover:bg-amber-50"
                      >
                        <Clock className="h-3 w-3 mr-1" />
                        Pause
                      </Button>
                    )}
                    
                    {/* End Shift Button */}
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleEndShift}
                      className="h-7 text-xs"
                    >
                      <X className="h-3 w-3 mr-1" />
                      End
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleStartShift}
                    className="h-7 text-xs bg-green-600 hover:bg-green-700"
                  >
                    <PlayCircle className="h-3 w-3 mr-1" />
                    Start Shift
                  </Button>
                )}
              </div>
            </div>
          )}
          
          {/* Compact Metrics */}
          <div className="grid grid-cols-4 gap-2">
            <div className="text-center p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
              <p className="text-lg font-bold text-blue-600">{displayDays.length}</p>
              <p className="text-xs text-muted-foreground">Days</p>
            </div>
            <div className="text-center p-2 bg-green-50 dark:bg-green-900/20 rounded">
              <p className="text-lg font-bold text-green-600">{recap.total_hours}h</p>
              <p className="text-xs text-muted-foreground">Hours</p>
            </div>
            <div className="text-center p-2 bg-amber-50 dark:bg-amber-900/20 rounded">
              <p className="text-lg font-bold text-amber-600">{recap.avg_hours_per_day}h</p>
              <p className="text-xs text-muted-foreground">Avg</p>
            </div>
            <div className="text-center p-2 bg-purple-50 dark:bg-purple-900/20 rounded">
              <p className="text-lg font-bold text-purple-600">{recap.tasks_completed}</p>
              <p className="text-xs text-muted-foreground">Tasks</p>
            </div>
          </div>
          
          {/* Compact Daily List - Expandable */}
          <div className="space-y-1 max-h-[400px] overflow-y-auto">
            {displayDays.map(dayData => {
              const isExpanded = expandedDays[dayData.dayKey];
              // For editors, use their own activeSession. For viewers, use teamActiveSessions
              const anyActiveSession = canEdit ? activeSession : teamActiveSessions[0];
              
              // Check if this day has active session time
              const hasActiveTimeForDay = dayHasActiveTime(dayData.dayKey);
              const isToday = dayData.dayKey === todayKey;
              const isTodayAndActive = isToday && anyActiveSession?.status === 'active';
              const isTodayAndPaused = isToday && anyActiveSession?.status === 'paused';
              const isPastDayWithActiveTime = hasActiveTimeForDay && !isToday;
              const isEmptyDay = dayData.isEmpty && !hasActiveTimeForDay;
              
              // For past days, just use the recap's totalSeconds (it already includes active session time)
              // For today with active session, we show the live timer instead
              // Only add activeSecondsForDay if the day was created fresh (not from recap)
              const totalDaySeconds = dayData.totalSeconds;
              
              const { metrics } = dayData;
              
              // Style classes based on day state
              const borderClass = isTodayAndPaused 
                ? 'border-amber-500 dark:border-amber-600 border-2' 
                : isTodayAndActive 
                  ? 'border-green-500 dark:border-green-600 border-2' 
                  : isPastDayWithActiveTime 
                    ? 'border-blue-400 dark:border-blue-600' 
                    : isEmptyDay
                      ? 'border-dashed border-slate-300 dark:border-slate-600'
                      : 'dark:border-slate-700';
              
              const bgClass = isTodayAndPaused 
                ? 'bg-amber-50 dark:bg-amber-900/30' 
                : isTodayAndActive 
                  ? 'bg-green-50 dark:bg-green-900/30' 
                  : isPastDayWithActiveTime 
                    ? 'bg-blue-50 dark:bg-blue-900/20' 
                    : isEmptyDay
                      ? 'bg-slate-50/50 dark:bg-slate-800/50'
                      : 'bg-slate-50 dark:bg-slate-800';
              
              return (
                <div key={dayData.dayKey} className={`border rounded ${borderClass}`}>
                  <button
                    className={`w-full flex items-center justify-between p-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-700 ${bgClass} ${isExpanded ? '' : 'rounded'}`}
                    onClick={() => !isEmptyDay && toggleDay(dayData.dayKey)}
                    disabled={isEmptyDay}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`font-medium ${isEmptyDay ? 'text-muted-foreground' : 'dark:text-white'}`}>{format(dayData.date, 'EEE, MMM d')}</span>
                      {isTodayAndPaused && (
                        <Badge className="bg-amber-500 text-white text-[10px] flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          PAUSED
                        </Badge>
                      )}
                      {isTodayAndActive && (
                        <Badge className="bg-green-600 text-white text-[10px] animate-pulse flex items-center gap-1">
                          <PlayCircle className="h-3 w-3" />
                          LIVE
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5">
                      {/* Time Display - Live timer for today, total for other days */}
                      {isTodayAndActive || isTodayAndPaused ? (
                        <Badge className={`${isTodayAndPaused ? 'bg-amber-500' : 'bg-green-600'} text-white text-xs font-mono`}>
                          {canEdit ? formatLiveTimer(todayLiveTimer) : formatLiveTimer(teamTodayTimer)}
                        </Badge>
                      ) : isEmptyDay ? (
                        <Badge variant="outline" className="text-xs text-muted-foreground">—</Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs">{formatDuration(totalDaySeconds)}</Badge>
                      )}
                      
                      {/* Rich Metrics Icons */}
                      {metrics.tasksCompleted > 0 && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge variant="secondary" className="text-[10px] px-1.5">
                              <CheckCircle className="h-3 w-3 mr-0.5 text-green-600" />
                              {metrics.tasksCompleted}
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent>Tasks Completed</TooltipContent>
                        </Tooltip>
                      )}
                      {dayData.allEntries.length > 0 && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge variant="secondary" className="text-[10px] px-1.5">
                              <PlusCircle className="h-3 w-3 mr-0.5 text-blue-600" />
                              {dayData.allEntries.length}
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent>Items Added</TooltipContent>
                        </Tooltip>
                      )}
                      {metrics.featuresCreated > 0 && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge variant="secondary" className="text-[10px] px-1.5 bg-purple-100 dark:bg-purple-900/30">
                              <Sparkles className="h-3 w-3 mr-0.5 text-purple-600" />
                              {metrics.featuresCreated}
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent>Features</TooltipContent>
                        </Tooltip>
                      )}
                      {metrics.bugsFix > 0 && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge variant="secondary" className="text-[10px] px-1.5 bg-red-100 dark:bg-red-900/30">
                              <Bug className="h-3 w-3 mr-0.5 text-red-600" />
                              {metrics.bugsFix}
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent>Bugs Fixed</TooltipContent>
                        </Tooltip>
                      )}
                      {metrics.supportTickets > 0 && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge variant="secondary" className="text-[10px] px-1.5 bg-amber-100 dark:bg-amber-900/30">
                              <MessageSquare className="h-3 w-3 mr-0.5 text-amber-600" />
                              {metrics.supportTickets}
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent>Support Tickets</TooltipContent>
                        </Tooltip>
                      )}
                      {dayData.hasBlockers && <Badge variant="destructive" className="text-xs">!</Badge>}
                      {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                    </div>
                  </button>
                
                {isExpanded && (
                  <div className="p-2 pb-3 space-y-2 bg-white dark:bg-slate-900 border-t dark:border-slate-700">
                    {/* Edit and Delete buttons - Only show if user can edit */}
                    {canEdit && (
                      <div className="flex justify-between">
                        {/* Delete button - only show for completed sessions */}
                        {!dayData.isActiveOnly && dayData.sessions[0]?.id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteSession(dayData.sessions[0].id);
                            }}
                            className="h-6 px-2 text-xs text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                          >
                            <Trash2 className="h-3 w-3 mr-1" />
                            Delete
                          </Button>
                        )}
                        {dayData.isActiveOnly && <div />}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDay(dayData, dayData.sessions[0])}
                          className="h-6 px-2 text-xs"
                          disabled={dayData.isActiveOnly}
                        >
                          <Pencil className="h-3 w-3 mr-1" />
                          Edit
                        </Button>
                      </div>
                    )}
                    
                    {/* Entries - Click to edit only if canEdit */}
                    {dayData.allEntries.length > 0 ? (
                      dayData.allEntries.map((entry, idx) => (
                        <div 
                          key={entry.id || idx} 
                          className={`p-2 bg-slate-50 dark:bg-slate-800 rounded text-xs ${canEdit && entry.type !== 'summary' ? 'cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors' : ''}`}
                          onClick={() => canEdit && entry.type !== 'summary' && entry.id && openEditEntry(entry)}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <p className="dark:text-white whitespace-pre-wrap flex-1">{entry.description}</p>
                            {canEdit && entry.type !== 'summary' && entry.id && (
                              <Pencil className="h-3 w-3 text-muted-foreground flex-shrink-0 mt-0.5" />
                            )}
                          </div>
                          <div className="flex items-center gap-1 mt-1">
                            {entry.entry_type && (
                              <Badge variant="outline" className="text-[10px]">
                                {entry.entry_type === 'task_completed' && '✓'}
                                {entry.entry_type === 'feature' && '✨'}
                                {entry.entry_type === 'bug_fix' && '🐛'}
                                {entry.entry_type === 'support_ticket' && '💬'}
                                {entry.entry_type === 'task_added' && '+'}
                                {entry.entry_type === 'note' && '📝'}
                              </Badge>
                            )}
                            {entry.hub_name && <Badge variant="secondary" className="text-[10px]">{entry.hub_name}</Badge>}
                            {entry.is_mvp && <Badge className="text-[10px] bg-orange-500">MVP</Badge>}
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-xs text-muted-foreground text-center py-2">No entries</p>
                    )}
                    
                    {/* Blockers */}
                    {dayData.allBlockers.length > 0 && (
                      <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded border-l-2 border-red-500 text-xs">
                        <p className="text-red-700 dark:text-red-300">{dayData.allBlockers.join('; ')}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Edit Day Dialog */}
        <Dialog open={!!editingDay} onOpenChange={(open) => !open && setEditingDay(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                Edit Day - {editingDay && format(editingDay.date, 'EEEE, MMM d, yyyy')}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              {/* Quick Presets */}
              <div>
                <Label className="text-xs text-muted-foreground mb-2 block">Quick Presets</Label>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => applyTimePreset(9, 0, 17, 0)}>
                    9 AM - 5 PM
                  </Button>
                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => applyTimePreset(8, 0, 16, 0)}>
                    8 AM - 4 PM
                  </Button>
                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => applyTimePreset(8, 0, 17, 0)}>
                    8 AM - 5 PM
                  </Button>
                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => applyTimePreset(9, 0, 18, 0)}>
                    9 AM - 6 PM
                  </Button>
                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => applyTimePreset(10, 0, 18, 0)}>
                    10 AM - 6 PM
                  </Button>
                </div>
              </div>

              {/* Time Fields */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Time Started
                  </Label>
                  <Input
                    type="datetime-local"
                    value={dayEditForm.started_at}
                    onChange={(e) => setDayEditForm(prev => ({ ...prev, started_at: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Time Ended
                  </Label>
                  <Input
                    type="datetime-local"
                    value={dayEditForm.ended_at}
                    onChange={(e) => setDayEditForm(prev => ({ ...prev, ended_at: e.target.value }))}
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Duration Display */}
              {calculateDuration() && (
                <div className="flex items-center justify-center p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <Clock className="h-4 w-4 text-green-600 mr-2" />
                  <span className="text-sm font-medium text-green-700 dark:text-green-300">
                    Duration: {calculateDuration().formatted}
                  </span>
                </div>
              )}
              
              <Separator />
              
              <div>
                <Label className="text-sm">Summary</Label>
                <Textarea
                  placeholder="What was done..."
                  value={dayEditForm.summary}
                  onChange={(e) => setDayEditForm(prev => ({ ...prev, summary: e.target.value }))}
                  className="mt-1 h-20"
                />
              </div>
              <div>
                <Label className="text-sm">What&apos;s Next</Label>
                <Textarea
                  placeholder="Upcoming..."
                  value={dayEditForm.whats_next}
                  onChange={(e) => setDayEditForm(prev => ({ ...prev, whats_next: e.target.value }))}
                  className="mt-1 h-16"
                />
              </div>
              <div>
                <Label className="text-sm text-red-600">Blockers</Label>
                <Textarea
                  placeholder="Any blockers..."
                  value={dayEditForm.blockers}
                  onChange={(e) => setDayEditForm(prev => ({ ...prev, blockers: e.target.value }))}
                  className="mt-1 h-16 border-red-200"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => setEditingDay(null)}>Cancel</Button>
              <Button size="sm" onClick={saveDay}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Entry Dialog */}
        <Dialog open={showAddEntry} onOpenChange={setShowAddEntry}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5 text-blue-600" />
                Add Entry
              </DialogTitle>
              <DialogDescription>
                Log what you&apos;re working on or have completed
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              {/* Entry Type Selection */}
              <div>
                <Label className="text-sm mb-2 block">Entry Type</Label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'task_completed', label: 'Task Done', icon: CheckCircle, color: 'text-green-600' },
                    { value: 'feature', label: 'Feature', icon: Sparkles, color: 'text-purple-600' },
                    { value: 'bug_fix', label: 'Bug Fix', icon: Bug, color: 'text-red-600' },
                    { value: 'support_ticket', label: 'Support', icon: MessageSquare, color: 'text-amber-600' },
                    { value: 'task_added', label: 'Task Added', icon: PlusCircle, color: 'text-blue-600' },
                    { value: 'note', label: 'Note', icon: FileText, color: 'text-slate-600' },
                  ].map(({ value, label, icon: Icon, color }) => (
                    <Button
                      key={value}
                      variant={addEntryForm.entry_type === value ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setAddEntryForm(prev => ({ ...prev, entry_type: value }))}
                      className={`h-auto py-2 flex-col gap-1 ${addEntryForm.entry_type === value ? '' : color}`}
                    >
                      <Icon className="h-4 w-4" />
                      <span className="text-[10px]">{label}</span>
                    </Button>
                  ))}
                </div>
              </div>
              
              {/* Description */}
              <div>
                <Label className="text-sm">Description *</Label>
                <Textarea
                  placeholder="What did you work on?"
                  value={addEntryForm.description}
                  onChange={(e) => setAddEntryForm(prev => ({ ...prev, description: e.target.value }))}
                  className="mt-1 h-24"
                />
              </div>
              
              {/* Hub Selection */}
              <div>
                <Label className="text-sm">Hub (optional)</Label>
                <select
                  value={addEntryForm.hub_id}
                  onChange={(e) => setAddEntryForm(prev => ({ ...prev, hub_id: e.target.value }))}
                  className="mt-1 w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="">Select Hub...</option>
                  <option value="call_center">Call Center</option>
                  <option value="sales">Sales</option>
                  <option value="operations">Operations</option>
                  <option value="marketing">Marketing</option>
                  <option value="platform">Platform</option>
                </select>
              </div>
              
              {/* MVP Tag */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_mvp"
                  checked={addEntryForm.is_mvp}
                  onChange={(e) => setAddEntryForm(prev => ({ ...prev, is_mvp: e.target.checked }))}
                  className="h-4 w-4 rounded"
                />
                <Label htmlFor="is_mvp" className="text-sm cursor-pointer">MVP Related</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => setShowAddEntry(false)}>Cancel</Button>
              <Button size="sm" onClick={handleAddEntry}>
                <Plus className="h-3 w-3 mr-1" />
                Add Entry
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Entry Dialog */}
        <Dialog open={!!editingEntry} onOpenChange={(open) => !open && setEditingEntry(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Pencil className="h-5 w-5 text-blue-600" />
                Edit Entry
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              {/* Entry Type Selection */}
              <div>
                <Label className="text-sm mb-2 block">Entry Type</Label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'task_completed', label: 'Task Done', icon: CheckCircle, color: 'text-green-600' },
                    { value: 'feature', label: 'Feature', icon: Sparkles, color: 'text-purple-600' },
                    { value: 'bug_fix', label: 'Bug Fix', icon: Bug, color: 'text-red-600' },
                    { value: 'support_ticket', label: 'Support', icon: MessageSquare, color: 'text-amber-600' },
                    { value: 'task_added', label: 'Task Added', icon: PlusCircle, color: 'text-blue-600' },
                    { value: 'note', label: 'Note', icon: FileText, color: 'text-slate-600' },
                  ].map(({ value, label, icon: Icon, color }) => (
                    <Button
                      key={value}
                      variant={editEntryForm.entry_type === value ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setEditEntryForm(prev => ({ ...prev, entry_type: value }))}
                      className={`h-auto py-2 flex-col gap-1 ${editEntryForm.entry_type === value ? '' : color}`}
                    >
                      <Icon className="h-4 w-4" />
                      <span className="text-[10px]">{label}</span>
                    </Button>
                  ))}
                </div>
              </div>
              
              {/* Description */}
              <div>
                <Label className="text-sm">Description *</Label>
                <Textarea
                  placeholder="What did you work on?"
                  value={editEntryForm.description}
                  onChange={(e) => setEditEntryForm(prev => ({ ...prev, description: e.target.value }))}
                  className="mt-1 h-24"
                />
              </div>
              
              {/* Hub Selection */}
              <div>
                <Label className="text-sm">Hub (optional)</Label>
                <select
                  value={editEntryForm.hub_id}
                  onChange={(e) => setEditEntryForm(prev => ({ ...prev, hub_id: e.target.value }))}
                  className="mt-1 w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="">Select Hub...</option>
                  <option value="call_center">Call Center</option>
                  <option value="sales">Sales</option>
                  <option value="operations">Operations</option>
                  <option value="marketing">Marketing</option>
                  <option value="platform">Platform</option>
                </select>
              </div>
              
              {/* MVP Tag */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="edit_is_mvp"
                  checked={editEntryForm.is_mvp}
                  onChange={(e) => setEditEntryForm(prev => ({ ...prev, is_mvp: e.target.checked }))}
                  className="h-4 w-4 rounded"
                />
                <Label htmlFor="edit_is_mvp" className="text-sm cursor-pointer">MVP Related</Label>
              </div>
            </div>
            <DialogFooter className="flex justify-between">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => editingEntry?.id && handleDeleteEntry(editingEntry.id)}
                className="text-red-500 hover:text-red-600 hover:bg-red-50"
              >
                <Trash2 className="h-3 w-3 mr-1" />
                Delete
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setEditingEntry(null)}>Cancel</Button>
                <Button size="sm" onClick={handleUpdateEntry}>
                  <Save className="h-3 w-3 mr-1" />
                  Save
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      </TooltipProvider>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="h-5 w-5 text-indigo-600" />
              Work Recap
            </CardTitle>
            <CardDescription>
              {format(dateRange.start, 'MMM d')} - {format(dateRange.end, 'MMM d, yyyy')}
            </CardDescription>
          </div>
          
          {/* Date Filter Controls */}
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
              <Button
                variant={dateFilter === 'this_week' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setDateFilter('this_week')}
                className="h-7 text-xs"
              >
                This Week
              </Button>
              <Button
                variant={dateFilter === 'last_week' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setDateFilter('last_week')}
                className="h-7 text-xs"
              >
                Last Week
              </Button>
              <Button
                variant={dateFilter === 'this_month' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setDateFilter('this_month')}
                className="h-7 text-xs"
              >
                This Month
              </Button>
              <Button
                variant={dateFilter === 'last_month' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setDateFilter('last_month')}
                className="h-7 text-xs"
              >
                Last Month
              </Button>
            </div>
            
            {/* Custom Date Range */}
            <div className="flex items-center gap-1">
              <Input
                type="date"
                value={customDateRange.start}
                onChange={(e) => {
                  setCustomDateRange(prev => ({ ...prev, start: e.target.value }));
                  setDateFilter('custom');
                }}
                className="h-7 text-xs w-32"
              />
              <span className="text-xs text-muted-foreground">to</span>
              <Input
                type="date"
                value={customDateRange.end}
                onChange={(e) => {
                  setCustomDateRange(prev => ({ ...prev, end: e.target.value }));
                  setDateFilter('custom');
                }}
                className="h-7 text-xs w-32"
              />
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Metrics Summary */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <p className="text-2xl font-bold text-blue-600">{groupedByDay.length}</p>
            <p className="text-xs text-muted-foreground">Days Worked</p>
          </div>
          <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <p className="text-2xl font-bold text-green-600">{recap.total_hours}h</p>
            <p className="text-xs text-muted-foreground">Total Hours</p>
          </div>
          <div className="text-center p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
            <p className="text-2xl font-bold text-amber-600">{recap.avg_hours_per_day}h</p>
            <p className="text-xs text-muted-foreground">Avg/Day</p>
          </div>
          <div className="text-center p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
            <p className="text-2xl font-bold text-purple-600">{recap.tasks_completed}</p>
            <p className="text-xs text-muted-foreground">Tasks Done</p>
          </div>
        </div>

        {/* Auto Summary */}
        <div className="mb-6 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="h-4 w-4 text-slate-500" />
            <span className="text-sm font-medium dark:text-white">Summary</span>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-300">{recap.auto_summary}</p>
        </div>

        {/* Daily Breakdown - Grouped by Day */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="h-4 w-4 text-slate-500" />
            <span className="text-sm font-medium dark:text-white">Daily Breakdown</span>
          </div>
          
          {groupedByDay.length > 0 ? (
            groupedByDay.map(dayData => {
              const isExpanded = expandedDays[dayData.dayKey];
              
              return (
                <div key={dayData.dayKey} className="border dark:border-slate-700 rounded-lg overflow-hidden">
                  <button
                    className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                    onClick={() => toggleDay(dayData.dayKey)}
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-medium dark:text-white">
                        {format(dayData.date, 'EEEE, MMM d')}
                      </span>
                      <Badge variant="outline">
                        <Clock className="h-3 w-3 mr-1" />
                        {formatDuration(dayData.totalSeconds)}
                      </Badge>
                      {dayData.totalTasks > 0 && (
                        <Badge className="bg-green-600">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          {dayData.totalTasks} tasks
                        </Badge>
                      )}
                      {dayData.allEntries.length > 0 && (
                        <Badge variant="secondary">
                          {dayData.allEntries.length} updates
                        </Badge>
                      )}
                      {dayData.hasBlockers && (
                        <Badge variant="destructive">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          Blockers
                        </Badge>
                      )}
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                  </button>
                  
                  {isExpanded && (
                    <div className="px-4 pb-4 space-y-3 bg-slate-50 dark:bg-slate-800/50">
                      {/* Edit button for the day */}
                      <div className="flex justify-end pt-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditDay(dayData, dayData.sessions[0]);
                          }}
                          className="text-xs text-muted-foreground hover:text-foreground"
                        >
                          <Pencil className="h-3 w-3 mr-1" />
                          Edit Day
                        </Button>
                      </div>
                      
                      {/* Entries for this day */}
                      {dayData.allEntries.length > 0 && (
                        <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">Updates</Label>
                          {dayData.allEntries.map((entry, idx) => (
                            <div key={entry.id || idx} className="p-3 bg-white dark:bg-slate-900 rounded-lg border dark:border-slate-700">
                              <p className="text-sm dark:text-white whitespace-pre-wrap">{entry.description}</p>
                              <div className="flex items-center gap-2 mt-2">
                                {entry.hub_name && (
                                  <Badge variant="secondary" className="text-xs">{entry.hub_name}</Badge>
                                )}
                                {entry.is_mvp && (
                                  <Badge className="text-xs bg-orange-500">MVP</Badge>
                                )}
                                {entry.type === 'summary' && (
                                  <Badge variant="outline" className="text-xs">End of Day Summary</Badge>
                                )}
                                <span className="text-xs text-muted-foreground">
                                  {entry.created_at && formatDistanceToNow(new Date(entry.created_at), { addSuffix: true })}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Blockers for this day */}
                      {dayData.allBlockers.length > 0 && (
                        <div className="space-y-2">
                          <Label className="text-xs text-red-600 dark:text-red-400">Blockers</Label>
                          {dayData.allBlockers.map((blocker, idx) => (
                            <div key={idx} className="p-2 bg-red-50 dark:bg-red-900/20 rounded border-l-4 border-red-500">
                              <p className="text-sm text-red-700 dark:text-red-300">{blocker}</p>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Show if no entries */}
                      {dayData.allEntries.length === 0 && dayData.allBlockers.length === 0 && (
                        <div className="text-center py-4 text-muted-foreground">
                          <p className="text-sm">No detailed entries for this day</p>
                          <Button
                            variant="outline"
                            size="sm"
                            className="mt-2"
                            onClick={() => openEditDay(dayData, dayData.sessions[0])}
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            Add Details
                          </Button>
                        </div>
                      )}
                      
                      {/* Sessions info */}
                      {dayData.sessions.length > 1 && (
                        <p className="text-xs text-muted-foreground mt-2">
                          {dayData.sessions.length} work sessions on this day
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="text-center py-6 text-muted-foreground">
              <Calendar className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No work days logged this week</p>
            </div>
          )}
        </div>

        {/* Completed Tasks */}
        {recap.completed_tasks && recap.completed_tasks.length > 0 && (
          <>
            <Separator className="my-4" />
            <div>
              <div className="flex items-center gap-2 mb-3">
                <ListChecks className="h-4 w-4 text-green-600" />
                <span className="text-sm font-medium dark:text-white">
                  Completed Tasks ({recap.completed_tasks.length})
                </span>
              </div>
              <div className="space-y-2">
                {recap.completed_tasks.slice(0, 10).map(task => (
                  <div key={task.id} className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                    <span className="dark:text-white">{task.title}</span>
                    {task.tags?.filter(t => t.startsWith('hub:')).map(tag => (
                      <Badge key={tag} variant="outline" className="text-xs">
                        {tag.split(':')[1]}
                      </Badge>
                    ))}
                  </div>
                ))}
                {recap.completed_tasks.length > 10 && (
                  <p className="text-xs text-muted-foreground">
                    +{recap.completed_tasks.length - 10} more tasks
                  </p>
                )}
              </div>
            </div>
          </>
        )}

        {/* Blockers Summary */}
        {recap.all_blockers && recap.all_blockers.length > 0 && (
          <>
            <Separator className="my-4" />
            <div>
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="h-4 w-4 text-red-500" />
                <span className="text-sm font-medium dark:text-white">
                  Blockers Reported ({recap.all_blockers.length})
                </span>
              </div>
              <div className="space-y-2">
                {recap.all_blockers.map((blocker, idx) => (
                  <div key={idx} className="p-2 bg-red-50 dark:bg-red-900/20 rounded text-sm text-red-700 dark:text-red-300">
                    {blocker}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </CardContent>

      {/* Edit Day Dialog */}
      <Dialog open={!!editingDay} onOpenChange={(open) => !open && setEditingDay(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Edit Day - {editingDay && format(editingDay.date, 'EEEE, MMM d, yyyy')}
            </DialogTitle>
            <DialogDescription>
              Update the time, summary and details for this work day
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Quick Presets */}
            <div>
              <Label className="text-xs text-muted-foreground mb-2 block">Quick Presets</Label>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" className="h-8" onClick={() => applyTimePreset(9, 0, 17, 0)}>
                  9 AM - 5 PM
                </Button>
                <Button variant="outline" size="sm" className="h-8" onClick={() => applyTimePreset(8, 0, 16, 0)}>
                  8 AM - 4 PM
                </Button>
                <Button variant="outline" size="sm" className="h-8" onClick={() => applyTimePreset(8, 0, 17, 0)}>
                  8 AM - 5 PM
                </Button>
                <Button variant="outline" size="sm" className="h-8" onClick={() => applyTimePreset(9, 0, 18, 0)}>
                  9 AM - 6 PM
                </Button>
                <Button variant="outline" size="sm" className="h-8" onClick={() => applyTimePreset(10, 0, 18, 0)}>
                  10 AM - 6 PM
                </Button>
              </div>
            </div>

            {/* Time Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  Time Started
                </Label>
                <Input
                  type="datetime-local"
                  value={dayEditForm.started_at}
                  onChange={(e) => setDayEditForm(prev => ({ ...prev, started_at: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  Time Ended
                </Label>
                <Input
                  type="datetime-local"
                  value={dayEditForm.ended_at}
                  onChange={(e) => setDayEditForm(prev => ({ ...prev, ended_at: e.target.value }))}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Duration Display */}
            {calculateDuration() && (
              <div className="flex items-center justify-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                <Clock className="h-5 w-5 text-green-600 mr-2" />
                <span className="text-base font-semibold text-green-700 dark:text-green-300">
                  Duration: {calculateDuration().formatted}
                </span>
              </div>
            )}
            
            <Separator />
            
            <div>
              <Label>What was accomplished</Label>
              <Textarea
                placeholder="Summary of work done..."
                value={dayEditForm.summary}
                onChange={(e) => setDayEditForm(prev => ({ ...prev, summary: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div>
              <Label>What&apos;s Next</Label>
              <Textarea
                placeholder="Upcoming work..."
                value={dayEditForm.whats_next}
                onChange={(e) => setDayEditForm(prev => ({ ...prev, whats_next: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-red-600">Blockers</Label>
              <Textarea
                placeholder="Any blockers..."
                value={dayEditForm.blockers}
                onChange={(e) => setDayEditForm(prev => ({ ...prev, blockers: e.target.value }))}
                className="mt-1 border-red-200 dark:border-red-800"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingDay(null)}>Cancel</Button>
            <Button onClick={saveDay}>
              <Save className="h-4 w-4 mr-1" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default WeeklyRecapSection;
